import React, { useEffect, useState } from "react";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import { Container } from "reactstrap";
import Alerts from "../../molecules/Alerts";
import { v4 } from "uuid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faUser,
  faBuilding,
  faPaperclip,
  faFileAlt,
  faCheckCircle,
  faBell,
  faPen,
} from "@fortawesome/free-solid-svg-icons";
import {
  getAccount,
  validateRequestor,
  sendBankToVendor,
  reopenForm,
  financeApproverDataLoader,
  lineManagerDataLoader,
} from "../../../utils/queries";
import { RequestHistoryType } from "../../../utils/common";
import { ChangeApproverModal } from "./Modal/ChangeApproverModal";
import { trackPromise } from "react-promise-tracker";
import { SendBackToVendorModal } from "./Modal/SendBackToVendorModal";
import { ReopenModal } from "./Modal/ReopenModal";
import { RequestModal } from "./Modal/RequestModal";
import { ReminderModal } from "./Modal/ReminderModal";
import { ValidateModal } from "./Modal/ValidateModal";
import "./RequestDetailPage.css";

const statusColorMap = {
  "Completed": "rdp-status--approved",
  "Form requested": "rdp-status--requested",
  "Form submitted": "rdp-status--requested",
  "Form resubmitted": "rdp-status--resubmitted",
  "Send back by requestor": "rdp-status--sendback",
  "Reopen": "rdp-status--sendback",
  "Validated by requestor": "rdp-status--resubmitted",
  "Cancelled": "rdp-status--rejected",
  "Rejected by line manager": "rdp-status--rejected",
  "Rejected by finance": "rdp-status--rejected",
  "Approved by line manager": "rdp-status--approved",
  "Approved by finance": "rdp-status--approved",
};

const SectionCard = ({ icon, title, badge, children }) => (
  <div className="rdp-section-card">
    <div className="rdp-section-card__header">
      <div className="rdp-section-card__title">
        {icon && <FontAwesomeIcon icon={icon} />}
        <span>{title}</span>
      </div>
      {badge != null && (
        <span className="rdp-section-card__badge">{badge}</span>
      )}
    </div>
    <div className="rdp-section-card__body">{children}</div>
  </div>
);

const DataRow = ({ label, value, onEdit }) => (
  <div className="rdp-detail-row">
    <span className="rdp-detail-label">{label}</span>
    <span className="rdp-detail-value">{value || "-"}</span>
    {onEdit && (
      <button className="rdp-edit-btn" type="button" onClick={onEdit}>
        <FontAwesomeIcon icon={faPen} />
      </button>
    )}
  </div>
);

const bField = (label, value, onEdit) => (
  <div className="rdp-vbdm__field">
    <div className="rdp-vbdm__field-label">{label}</div>
    <div className={`rdp-vbdm__field-value${!value ? " rdp-vbdm__field-value--empty" : ""}`}>
      {value || "—"}
    </div>
    {onEdit && (
      <button className="rdp-edit-btn" type="button" onClick={onEdit} style={{ position: "absolute", top: 8, right: 8 }}>
        <FontAwesomeIcon icon={faPen} />
      </button>
    )}
  </div>
);

const fmt = (date) =>
  date ? moment(date).format("DD-MM-YYYY HH:mm") : "-";

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const RequestDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [account, setAccount] = useState(null);
  const [accountTypes, setAccountTypes] = useState("");
  const [banks, setBanks] = useState([]);
  const [files, setFiles] = useState([]);
  const [formConfirmed, setFormConfirmed] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggleSendBack, setToggleSendBack] = useState(false);
  const [toggleReopen, setToggleReopen] = useState(false);
  const [toggleRequestModal, setToggleRequestModal] = useState(false);
  const [toggleReminderModal, setToggleReminderModal] = useState(false);
  const [toggleValidateModal, setToggleValidateModal] = useState(false);
  const [accountTypeIds, setAccountTypeIds] = useState([]);
  const [changeType, setChangeType] = useState(null);
  const [toggleChangeModal, setToggleChangeModal] = useState(false);

  const addAlert = (message, color = "success") =>
    setAlerts((prev) => [...prev, { message, id: v4(), color }]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const result = await trackPromise(getAccount(id));
        setAccount(result.data.request);
        setAccountTypes(result.data.accountTypes);
        setBanks(result.data.bankDataList || []);
        setFiles(result.data.files || []);
        setAccountTypeIds(result.data.accountTypeIdList || []);
        if (result.data.request.verballyConfirmed) setFormConfirmed(true);
      } catch (err) {
        addAlert("Failed to load request.", "danger");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const reminderStatuses = [
    "Form requested",
    "Validated by requestor",
    "Send back by requestor",
    "Approved by line manager",
    "Reopen",
  ];
  const canSendReminder = () => reminderStatuses.includes(account?.status);

  const canValidate = () => account?.status === "Form submitted" && formConfirmed;
  const canCancel = () =>
    account?.status !== "Cancelled" &&
    account?.status !== "Validated by requestor" &&
    account?.status !== "Completed";
  const canSendBack = () => account?.status === "Form submitted";
  const canReopen = () => account?.status === "Cancelled";

  const onValidate = async () => {
    try {
      const result = await validateRequestor(account.id);
      setAccount(result.data);
      addAlert("Request validated successfully.");
    } catch (err) {
      addAlert("Failed to validate.", "danger");
    }
  };

  const onCancelUpdate = (updated) => {
    setAccount(updated);
    addAlert("Request cancelled.");
  };

  const onReminderSent = (updated) => {
    if (updated) setAccount(updated);
    addAlert("Reminder sent successfully.");
  };

  const onSendBack = async (accountId, additionalText) => {
    try {
      const result = await sendBankToVendor(accountId, additionalText);
      setAccount(result.data);
      addAlert("Sent back to vendor successfully.");
    } catch (err) {
      addAlert("Failed to send back.", "danger");
    }
  };

  const onReopen = async (accountId, reason) => {
    try {
      const result = await reopenForm(accountId, reason);
      setAccount(result.data);
      addAlert("Form reopened successfully.");
    } catch (err) {
      addAlert("Failed to reopen.", "danger");
    }
  };

  const openChangeModal = (type) => {
    setChangeType(type);
    setToggleChangeModal(true);
  };

  const onUpdateApprover = (response) => {
    if (response?.data) setAccount(response.data);
    addAlert("Updated successfully.");
  };

  if (loading) {
    return (
      <Container className="rdp-container">
        <div className="rdp-loading">Loading...</div>
      </Container>
    );
  }

  if (!account) {
    return (
      <Container className="rdp-container">
        <div className="rdp-loading">Request not found.</div>
      </Container>
    );
  }

  const heroCls = statusColorMap[account.status] || "rdp-status--default";

  return (
    <Container className="rdp-container">
      <Alerts alerts={alerts} setAlerts={setAlerts} />

      <button className="rdp-back-btn" onClick={() => navigate("/history")}>
        <FontAwesomeIcon icon={faArrowLeft} /> Back to History
      </button>

      {/* Hero card */}
      <div className="rdp-hero">
        <div className="rdp-hero__top">
          <div className="rdp-hero__identity">
            <div className="rdp-hero__avatar">{getInitials(account.name)}</div>
            <div>
              <div className="rdp-hero__name">{account.name || "-"}</div>
              <div className="rdp-hero__id">Request #{account.id}</div>
            </div>
          </div>
          <span className={"rdp-hero__status " + heroCls}>{account.status}</span>
        </div>

        <div className="rdp-hero__meta">
          <div className="rdp-hero__meta-item">
            <span className="rdp-hero__meta-label">Submitted</span>
            <span className="rdp-hero__meta-value">{fmt(account.formSubmittedDate)}</span>
          </div>
          <div className="rdp-hero__meta-item">
            <span className="rdp-hero__meta-label">Requestor</span>
            <span className="rdp-hero__meta-value">{account.requestedByName || "-"}</span>
          </div>
          <div className="rdp-hero__meta-item">
            <span className="rdp-hero__meta-label">Line Manager</span>
            <span className="rdp-hero__meta-value">{account.lineManagerToNotify || "-"}</span>
            <button className="rdp-edit-btn" type="button" onClick={() => openChangeModal(RequestHistoryType.lineManager)}>
              <FontAwesomeIcon icon={faPen} />
            </button>
          </div>
          <div className="rdp-hero__meta-item">
            <span className="rdp-hero__meta-label">Finance Approver</span>
            <span className="rdp-hero__meta-value">{account.financeApproverToNotify || "-"}</span>
            <button className="rdp-edit-btn" type="button" onClick={() => openChangeModal(RequestHistoryType.financeManager)}>
              <FontAwesomeIcon icon={faPen} />
            </button>
          </div>
        </div>

        <div className="rdp-hero__actions">
          <button className="rdp-btn rdp-btn--validate" onClick={() => setToggleValidateModal(true)} disabled={!canValidate()}>
            <FontAwesomeIcon icon={faCheckCircle} /> Validate
          </button>
          <button className="rdp-btn rdp-btn--cancel" onClick={() => setToggleRequestModal(true)} disabled={!canCancel()}>
            Cancel
          </button>
          <button className="rdp-btn rdp-btn--ghost-hero" onClick={() => setToggleReminderModal(true)} disabled={!canSendReminder()}>
            <FontAwesomeIcon icon={faBell} /> Send Reminder
          </button>
          <button className="rdp-btn rdp-btn--ghost-hero" onClick={() => setToggleSendBack(true)} disabled={!canSendBack()}>
            Send back to vendor
          </button>
          <button className="rdp-btn rdp-btn--ghost-hero" onClick={() => setToggleReopen(true)} disabled={!canReopen()}>
            Reopen form
          </button>
        </div>
      </div>

      {/* Verbal confirmation */}
      <div className={"rdp-verbal-card" + (formConfirmed ? " rdp-verbal-card--confirmed" : "")}>
        <div className="rdp-verbal-body">
          <span className="rdp-verbal-label-text">Verbal Confirmation</span>
          <span className="rdp-verbal-desc">Vendor bank details verbally confirmed by requestor</span>
        </div>
        <div className="rdp-verbal-toggle-col">
          <div
            className={"rdp-toggle" + (formConfirmed ? " rdp-toggle--on" : "")}
            onClick={() => { if (!account.verballyConfirmed) setFormConfirmed((v) => !v); }}
          >
            <div className="rdp-toggle__thumb" />
          </div>
        </div>
      </div>

      {/* Account Information */}
      <SectionCard icon={faUser} title="Account Information">
        <div className="rdp-vbdm__body">
          <div className="rdp-vbdm__section">
            <div className="rdp-vbdm__grid">
              {bField("Vendor Name", account.name)}
              {bField("Account Name", account.accountName || account.newAccountName)}
              {bField("Address Line 1", account.address1)}
              {bField("Address Line 2", account.address2)}
              {bField("City/Town", account.city)}
              {bField("Postal Code", account.zip)}
              {bField("Country", account.country?.countryName)}
              {bField("Phone", account.phone)}
              {bField("Contact Email", account.requestContactEmail, () => openChangeModal(RequestHistoryType.contactEmail))}
              {bField("Contact Mobile", account.requestContactMobile, () => openChangeModal(RequestHistoryType.contactMobile))}
              {bField("Sales Contact Name", account.salesContactName)}
              {bField("Sales Mobile", account.salesContactMobile)}
              {bField("Sales Email", account.salesEmail)}
              {bField("Finance Email", account.financeEmail)}
              {bField("Tax Reg. No.", account.taxRegistrationNumber)}
              {bField("Payment Term", account.paymentTermName)}
              {bField("Short Name", account.shortname)}
              {bField("Note", account.notes)}
              {bField("Account Type", accountTypes)}
            </div>
          </div>
        </div>
      </SectionCard>

      {banks.length > 0 && (
        <SectionCard icon={faBuilding} title="Bank Details">
          <div className="rdp-vbdm__body">
            {banks.map((bank, idx) => {
              const hasBeneficiaryWarning =
                ["IBAN", "BIC", "ABA ROUTING NUMBER"].includes(bank.type) &&
                bank.beneficiaryBankAccountName?.trim().toLowerCase() !== account.name?.trim().toLowerCase();
              return (
                <React.Fragment key={idx}>
                  {idx > 0 && <div className="rdp-vbdm__divider" />}
                  <div className="rdp-vbdm__section">
                    <div className="rdp-vbdm__section-label">
                      {bank.isPrimary && <span style={{ marginLeft: 8, background: "#e8f0fb", color: "#225A99", fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 10, textTransform: "uppercase" }}>Primary</span>}
                    </div>
                    {hasBeneficiaryWarning && (
                      <p style={{ margin: "0 0 10px", fontSize: 12, color: "#b91c1c" }}>Beneficiary name differs from vendor name</p>
                    )}

                    {/* IBAN Bank Fields */}
                    {bank.type === "IBAN" && (
                      <>
                        <div className="rdp-vbdm__grid">
                          {bField("Bank Identifier", bank.type)}
                          {bField("Beneficiary Name", bank.beneficiaryBankAccountName)}
                          {bField("Beneficiary Bank Name", bank.bank)}
                          {bField("IBAN Account Number", bank.iban)}
                          {bField("Beneficiary Bank Currency", bank.currency?.name)}
                          {bField("Beneficiary Bank SWIFT", bank.bic)}
                          {bField("Beneficiary Bank City", bank.city)}
                          {bField("Beneficiary Bank Postal Code", bank.zip)}
                          {bField("Beneficiary Bank Country", bank.country)}
                          {bField("Beneficiary Bank Country Code", bank.countryCode)}
                          {bField("Beneficiary Bank Branch", bank.branch)}
                        </div>
                        {bank.correspondentBankSwift && (
                          <>
                            <div className="rdp-vbdm__divider" />
                            <div className="rdp-vbdm__grid">
                              {bField("Correspondent Bank SWIFT", bank.correspondentBankSwift)}
                              {bField("Correspondent Bank Name", bank.correspondentBankName)}
                              {bField("Correspondent Bank Address", bank.correspondentBankAddress)}
                              {bField("Correspondent Bank City", bank.correspondentBankCity)}
                              {bField("Correspondent Bank Postal Code", bank.correspondentBankPostalCode)}
                              {bField("Correspondent Bank Country Name", bank.correspondentBankCountryName)}
                              {bField("Correspondent Bank Country Code", bank.correspondentBankCountryCode)}
                            </div>
                          </>
                        )}
                      </>
                    )}

                    {/* SWIFT Bank Fields */}
                    {bank.type === "BIC" && (
                      <>
                        <div className="rdp-vbdm__grid">
                          {bField("Bank Identifier", bank.type)}
                          {bField("Beneficiary Name", bank.beneficiaryBankAccountName)}
                          {bField("Account Number", bank.bankAccountNumber)}
                          {bField("Currency", bank.currency?.name)}
                        </div>
                        <div className="rdp-vbdm__divider" />
                        <div className="rdp-vbdm__grid">
                          {bField("Beneficiary Bank SWIFT", bank.bic)}
                          {bField("Beneficiary Bank Name", bank.bank)}
                          {bField("Beneficiary Bank Address", bank.address)}
                          {bField("Beneficiary Bank City", bank.city)}
                          {bField("Beneficiary Bank Postal Code", bank.zip)}
                          {bField("Beneficiary Bank Country Name", bank.country)}
                          {bField("Beneficiary Bank Country Code", bank.countryCode)}
                        </div>

                        {bank.correspondentBankSwift && (
                          <>
                            <div className="rdp-vbdm__divider" />
                            <div className="rdp-vbdm__grid">
                              {bField("Correspondent Bank SWIFT", bank.correspondentBankSwift)}
                              {bField("Correspondent Bank Name", bank.correspondentBankName)}
                              {bField("Correspondent Bank Address", bank.correspondentBankAddress)}
                              {bField("Correspondent Bank City", bank.correspondentBankCity)}
                              {bField("Correspondent Bank Postal Code", bank.correspondentBankPostalCode)}
                              {bField("Correspondent Bank Country Name", bank.correspondentBankCountryName)}
                              {bField("Correspondent Bank Country Code", bank.correspondentBankCountryCode)}
                            </div>
                          </>
                        )}
                      </>
                    )}

                    {/* ABA ROUTING NUMBER Bank Fields */}
                    {bank.type === "ABA ROUTING NUMBER" && (
                      <>
                        <div className="rdp-vbdm__grid">
                          {bField("Bank Identifier", bank.type)}
                          {bField("Beneficiary Name", bank.beneficiaryBankAccountName)}
                          {bField("Account Number", bank.bankAccountNumber)}
                          {bField("Currency", bank.currency?.name)}
                        </div>
                        <div className="rdp-vbdm__divider" />
                        <div className="rdp-vbdm__grid">
                          {bField("Beneficiary Bank ABA Routing Number", bank.abaRoutingNumber)}
                          {bField("Beneficiary Bank Name", bank.bank)}
                          {bField("Beneficiary Bank Address", bank.address)}
                          {bField("Beneficiary Bank City", bank.city)}
                          {bField("Beneficiary Bank Postal Code", bank.zip)}
                          {bField("Beneficiary Bank Country", bank.country)}
                        </div>
                        {bank.correspondentBankABARoutingNumber && (
                          <>
                            <div className="rdp-vbdm__divider" />
                            <div className="rdp-vbdm__grid">
                              {bField("Correspondent Bank ABA Routing Number", bank.correspondentBankABARoutingNumber)}
                              {bField("Correspondent Bank Name", bank.correspondentBankName)}
                              {bField("Correspondent Bank Address", bank.correspondentBankAddress)}
                              {bField("Correspondent Bank City", bank.correspondentBankCity)}
                              {bField("Correspondent Bank Postal Code", bank.correspondentBankPostalCode)}
                              {bField("Correspondent Bank Country Name", bank.correspondentBankCountryName)}
                            </div>
                          </>
                        )}
                      </>
                    )}

                    {/* REGNO/ACCOUNT Bank Fields */}
                    {bank.type === "REGNO/ACCOUNT" && (
                      <div className="rdp-vbdm__grid">
                        {bField("Bank Identifier", bank.type)}
                        {bField("Account Number", bank.bankAccountNumber)}
                        {bField("Registration Number", bank.regNo)}
                        {bField("Beneficiary Bank Name", bank.bank)}
                        {bField("Beneficiary Bank Address", bank.address)}
                        {bField("Beneficiary Bank City", bank.city)}
                        {bField("Beneficiary Bank Postal Code", bank.zip)}
                        {bField("Beneficiary Bank Country", bank.country)}
                        {bField("Beneficiary Bank SWIFT", bank.bic)}
                        {bField("Beneficiary Bank IBAN", bank.iban)}
                      </div>
                    )}

                    {/* FIK Bank Fields */}
                    {bank.type === "FIK" && (
                      <div className="rdp-vbdm__grid">
                        {bField("Bank Identifier", bank.type)}
                        {bField("Kortart", bank.fikKortart)}
                        {bField("Kreditor / GIRO", bank.fikNumber)}
                      </div>
                    )}

                    {bank.files?.length > 0 && (
                      <div className="rdp-vbdm__grid">
                        <div className="rdp-vbdm__field">
                          <div className="rdp-vbdm__field-label">File</div>
                          <div className="rdp-vbdm__field-value">
                            {bank.files.map((file, fi) => (
                              <a key={fi} className="rdp-file-item" href={file.url} target="_blank" rel="noopener noreferrer" style={{ display: "block" }}>
                                <FontAwesomeIcon icon={faPaperclip} /> {file.displayName}
                              </a>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </SectionCard>
      )}

      {/* Request Information */}
      <SectionCard icon={faFileAlt} title="Request Information">
        <div className="rdp-vbdm__body">
          <div className="rdp-vbdm__section">
            <div className="rdp-vbdm__grid">
              {bField("Request ID", String(account.id))}
              {bField("Status", account.status)}
              {bField("Business Justification", account.businessJustification)}
              {bField("Requestor name", account.requestedByName)}
              {bField("Requestor E-Mail", account.requestedByEmail)}
              {bField("Submitted", fmt(account.formSubmittedDate))}
              {bField("Line Manager e-mail", account.lineManagerToNotify)}
              {account.approvedByLineManagerName && bField("Approved by Line Manager", account.approvedByLineManagerName)}
              {account.approvedByLineManagerAt && bField("Approved by Line Manager date", fmt(account.approvedByLineManagerAt))}
              {account.rejectedByLineManagerName && bField("Rejected by Line Manager", account.rejectedByLineManagerName)}
              {account.rejectedByLineManagerName && bField("Rejected by Line Manager date", fmt(account.rejectedByLineManagerAt))}
              {bField("Finance Approver E-mail", account.financeApproverToNotify)}
              {account.approvedByFinanceName && bField("Approved by Finance", account.approvedByFinanceName)}
              {account.approvedByFinanceAt && bField("Approved by Finance date", fmt(account.approvedByFinanceAt))}
              {account.rejectedByFinanceName && bField("Rejected by Finance", account.rejectedByFinanceName)}
              {account.rejectedByFinanceName && bField("Rejected by Finance date", fmt(account.rejectedByFinanceAt))}
              {account.status === "Cancelled" && account.cancelledBy && bField("Cancelled By", account.cancelledBy)}
              {account.status === "Cancelled" && account.cancelledByReason && bField("Cancellation Reason", account.cancelledByReason)}
              {files.length > 0 && (
                <div className="rdp-vbdm__field">
                  <div className="rdp-vbdm__field-label">Attachments</div>
                  <div className="rdp-vbdm__field-value">
                    {files.map((file, i) => (
                      <a key={i} className="rdp-file-item" href={file.url} target="_blank" rel="noopener noreferrer">
                        <FontAwesomeIcon icon={faPaperclip} />
                        <span>{file.displayName || file.fileName}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </SectionCard>

      <ValidateModal
        toggle={toggleValidateModal}
        setToggle={setToggleValidateModal}
        onConfirm={onValidate}
      />
      <RequestModal
        toggle={toggleRequestModal}
        setToggle={setToggleRequestModal}
        account={account}
        onUpdateRequest={onCancelUpdate}
      />
      <ReminderModal
        toggle={toggleReminderModal}
        setToggle={setToggleReminderModal}
        account={account}
        onSent={onReminderSent}
      />
      <SendBackToVendorModal
        toggle={toggleSendBack}
        setToggle={setToggleSendBack}
        account={account}
        onSendBackToVendor={onSendBack}
      />
      <ReopenModal
        toggle={toggleReopen}
        setToggle={setToggleReopen}
        account={account}
        onReopen={onReopen}
      />
      <ChangeApproverModal
        account={account}
        toggleModal={toggleChangeModal}
        setToggleModal={setToggleChangeModal}
        type={changeType}
        onUpdateAccount={onUpdateApprover}
        dataLoader={
          changeType === RequestHistoryType.financeManager
            ? financeApproverDataLoader(accountTypeIds[0])
            : changeType === RequestHistoryType.lineManager
              ? lineManagerDataLoader(accountTypeIds[0])
              : { loader: () => Promise.resolve({ data: [] }), uniqueKey: "none" }
        }
        defaultValue={
          changeType === RequestHistoryType.contactEmail
            ? account?.requestContactEmail
            : changeType === RequestHistoryType.contactMobile
              ? account?.requestContactMobile
              : undefined
        }
      />
    </Container>
  );
};

export default RequestDetailPage;